/**
 * BACK-01/04/16 — botão voltar do Capacitor: overlay, guarda, rota pai e duplo voltar na raiz.
 */
import type { NavigateFunction } from 'react-router-dom'
import { resolveBackTarget } from '@/app/routeParentMap'
import { closeTopNativeOverlay } from '@/shared/lib/overlay/legacyNativeOverlayStack'
import { overlayManager } from '@/shared/lib/overlay/overlayManager'
import { exitOrMinimizeApp, onNativeBackButton } from '@/shared/lib/platform/backButton'
import { getUnsavedChangesBackCoordinator } from '@/shared/lib/unsavedChangesBackCoordinator'
import { useToastStore } from '@/shared/ui/toast/toastStore'
import { createBackToExit, type BackToExit } from './nativeBackToExit'

const createNativeBackToExit = (): BackToExit => createBackToExit({
  now: Date.now,
  toast: (message, durationMs) => {
    useToastStore.getState().add(message, { variant: 'backToExit', durationMs })
  },
  exit: () => {
    void exitOrMinimizeApp().catch(() => {
      useToastStore.getState().add('Não foi possível fechar o app. Tente novamente.', { variant: 'error' })
    })
  },
})

export const getNativeBackNavigationTarget = (pathname: string): string | null =>
  resolveBackTarget(pathname)

export interface RegisterNativeBackHandlerOptions {
  navigate: NavigateFunction
  getPathname: () => string
}

export const handleNativeBackButton = async (
  options: RegisterNativeBackHandlerOptions,
  backToExit: BackToExit = createNativeBackToExit(),
): Promise<boolean> => {
  if (overlayManager.size() > 0) {
    backToExit.reset()
    await overlayManager.requestCloseTop('back')
    return true
  }
  if (closeTopNativeOverlay()) {
    backToExit.reset()
    return true
  }

  const coordinator = getUnsavedChangesBackCoordinator()
  if (coordinator?.isDirty()) {
    backToExit.reset()
    const shouldLeave = await coordinator.confirmDiscard()
    if (!shouldLeave) return true
    coordinator.clearDirty()
  }

  const target = getNativeBackNavigationTarget(options.getPathname())
  if (target) {
    backToExit.reset()
    options.navigate(target)
    return true
  }

  backToExit.press()
  return true
}

/**
 * Gate do kill-switch RW-S02: só registra back hierárquico quando a flag está ativa.
 * `registerNativeBackHandler` em si só liga listener no Capacitor (`platform.isNative()`).
 */
export const shouldRegisterNativeBackHandler = (hierarchicalBackEnabled: boolean): boolean =>
  hierarchicalBackEnabled

/**
 * Registra o listener do botão voltar nativo. Retorna cleanup.
 * No navegador/PWA não registra nada e não altera o histórico (NAV-023).
 */
export const registerNativeBackHandler = (
  options: RegisterNativeBackHandlerOptions,
): (() => void) => {
  const backToExit = createNativeBackToExit()
  let handling = false
  return onNativeBackButton(() => {
    // Um gesto não deve atravessar uma confirmação assíncrona ainda em andamento.
    if (handling) return
    handling = true
    void handleNativeBackButton(options, backToExit).finally(() => { handling = false })
  })
}
